<script lang="ts">
  import { RefreshCw, Sun, Moon, Globe } from "lucide-svelte";
  import type { ServerLlmInfo } from "../../chat/types";
  import { i18n } from "../../chat/i18n.svelte";
  import { getChatContext } from "../../chat/chat-runtime-context";
  import { authStore } from "../../auth/auth-store";
  import { updateGlobalConfig } from "../../api/chat-api";

  const { t } = i18n;

  interface Props {
    serverLlmInfo: ServerLlmInfo | null;
    onRefresh: () => void;
    theme: "light" | "dark";
    toggleTheme: () => void;
  }

  let { serverLlmInfo, onRefresh, theme, toggleTheme }: Props = $props<Props>();
  const chat = getChatContext();
  const runtimeState = chat.state;

  let refreshing = $state(false);

  async function refresh() {
    refreshing = true;
    try {
      await Promise.resolve(onRefresh());
    } finally {
      refreshing = false;
    }
  }
</script>

<div class="flex-1 overflow-y-auto p-5 space-y-8" style="font-family: var(--chat-font-sans); background: var(--chat-bg);">
  <!-- User Preferences -->
  <div>
    <div style="font-size: 14px; font-weight: 700; color: var(--chat-brand-green); margin-bottom: 12px; letter-spacing: -0.01em; display: flex; align-items: center; gap: 8px;">
      <Globe size={14} />
      {t("userPreferences")}
    </div>
    
    <div style="background: var(--chat-bg-secondary); border: 1px solid var(--chat-bg-tertiary); border-radius: var(--chat-radius); box-shadow: var(--chat-shadow-sm); overflow: hidden;">
      <!-- Language Selector -->
      <div class="px-4 py-3 flex items-center justify-between">
        <span class="text-xs font-medium text-(--chat-text-primary)">{t("language")}</span>
        <select 
          bind:value={i18n.locale}
          onchange={(e) => i18n.setLocale((e.target as HTMLSelectElement).value as any)}
          class="text-xs bg-(--chat-bg-tertiary) border border-(--chat-border) rounded px-2 py-1 outline-none text-(--chat-text-primary)"
        >
          <option value="vi">Tiếng Việt</option>
          <option value="en">English</option>
        </select>
      </div>

      <!-- Theme Selector -->
      <div style="padding: 12px 16px; display: flex; items-center; justify-content: space-between; border-top: 1px solid var(--chat-bg-tertiary);">
        <span style="font-size: 13px; font-weight: 600; color: var(--chat-text-primary);">{t("theme")}</span>
        <button
          type="button"
          onclick={toggleTheme}
          style="
            display: flex; align-items: center; gap: 8px;
            padding: 6px 14px;
            border-radius: var(--chat-radius-pill);
            background: var(--chat-bg-tertiary);
            border: 1px solid var(--chat-bg-tertiary);
            color: var(--chat-text-primary);
            font-size: 12px; font-weight: 600;
            transition: transform 0.1s;
          "
          onmousedown={(e) => (e.currentTarget.style.transform = 'scale(var(--button-active-scale))')}
          onmouseup={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        >
          {#if theme === "dark"}
            <Moon size={13} style="color: var(--chat-accent);" />
            {t("darkMode")}
          {:else}
            <Sun size={13} style="color: #f59e0b;" />
            {t("lightMode")}
          {/if}
        </button>
      </div>

      <div style="padding: 12px 16px; display: flex; items-center; justify-content: space-between; border-top: 1px solid var(--chat-bg-tertiary); opacity: {$authStore.user?.isAdmin ? 1 : 0.6};">
        <div style="display: flex; flex-direction: column; gap: 2px;">
          <span style="font-size: 13px; font-weight: 600; color: var(--chat-text-primary);">{t("showAdvancedStats") || "Advanced Stats"}</span>
          {#if !$authStore.user?.isAdmin}
            <span style="font-size: 10px; color: var(--chat-text-muted); italic;">(Admin only)</span>
          {/if}
        </div>
        <button
          type="button"
          aria-label={($runtimeState.serverLlmInfo?.showAdvancedStats ? "Disable" : "Enable") + " advanced stats"}
          disabled={!$authStore.user?.isAdmin}
          onclick={async () => {
            if (!$authStore.user?.isAdmin) return;
            const newVal = !($runtimeState.serverLlmInfo?.showAdvancedStats);
            try {
              await updateGlobalConfig({ 'SHOW_ADVANCED_STATS': String(newVal) });
              // Refresh config after update
              await onRefresh();
            } catch (err) {
              console.error("Failed to update global config:", err);
            }
          }}
          style="
            position: relative;
            width: 36px; height: 20px;
            border-radius: 99px;
            background: {$runtimeState.serverLlmInfo?.showAdvancedStats ? 'var(--chat-accent)' : 'var(--chat-bg-tertiary)'};
            transition: background 0.2s;
            border: 1px solid var(--chat-border);
            cursor: {$authStore.user?.isAdmin ? 'pointer' : 'not-allowed'};
          "
        >
          <div style="
            position: absolute; top: 2px; left: {$runtimeState.serverLlmInfo?.showAdvancedStats ? '18px' : '2px'};
            width: 14px; height: 14px;
            background: white;
            border-radius: 50%;
            transition: left 0.2s;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          "></div>
        </button>
      </div>

      <div style="padding: 12px 16px; display: flex; items-center; justify-content: space-between; border-top: 1px solid var(--chat-bg-tertiary); opacity: {$authStore.user?.isAdmin ? 1 : 0.6};">
        <div style="display: flex; flex-direction: column; gap: 2px;">
          <span style="font-size: 13px; font-weight: 600; color: var(--chat-text-primary);">{t("showToolDetails") || "Tool Details"}</span>
          {#if !$authStore.user?.isAdmin}
            <span style="font-size: 10px; color: var(--chat-text-muted); italic;">(Admin only)</span>
          {/if}
        </div>
        <button
          type="button"
          aria-label={($runtimeState.serverLlmInfo?.showToolDetails ? "Disable" : "Enable") + " tool details"}
          disabled={!$authStore.user?.isAdmin}
          onclick={async () => {
            if (!$authStore.user?.isAdmin) return;
            const newVal = !($runtimeState.serverLlmInfo?.showToolDetails);
            try {
              await updateGlobalConfig({ 'SHOW_TOOL_DETAILS': String(newVal) });
              await onRefresh();
            } catch (err) {
              console.error("Failed to update global config:", err);
            }
          }}
          style="
            position: relative;
            width: 36px; height: 20px;
            border-radius: 99px;
            background: {$runtimeState.serverLlmInfo?.showToolDetails ? 'var(--chat-accent)' : 'var(--chat-bg-tertiary)'};
            transition: background 0.2s;
            border: 1px solid var(--chat-border);
            cursor: {$authStore.user?.isAdmin ? 'pointer' : 'not-allowed'};
          "
        >
          <div style="
            position: absolute; top: 2px; left: {$runtimeState.serverLlmInfo?.showToolDetails ? '18px' : '2px'};
            width: 14px; height: 14px;
            background: white;
            border-radius: 50%;
            transition: left 0.2s;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          "></div>
        </button>
      </div>
    </div>
  </div>

  {#if $authStore.user?.isAdmin}
    <div>
      <div style="font-size: 14px; font-weight: 700; color: var(--chat-brand-green); margin-bottom: 8px; letter-spacing: -0.01em;">
        {t("serverConfig")}
      </div>
      <p style="font-size: 13px; color: var(--chat-text-secondary); line-height: 1.5; margin-bottom: 16px;">
        {t("serverConfigDescription")}
      </p>

      <div
        style="background: var(--chat-bg-secondary); border: 1px solid var(--chat-bg-tertiary); border-radius: var(--chat-radius); box-shadow: var(--chat-shadow-sm); overflow: hidden;"
      >
        <div style="padding: 12px 16px; border-bottom: 1px solid var(--chat-bg-tertiary); display: flex; items-center; justify-content: space-between; background: var(--chat-bg-tertiary);">
          <span style="font-size: 11px; text-transform: uppercase; font-weight: 700; letter-spacing: 0.05em; color: var(--chat-brand-green);">{t("systemConfiguration")}</span>
          <button
            type="button"
            onclick={() => void refresh()}
            disabled={refreshing}
            style="
              display: flex; align-items: center; gap: 6px;
              font-size: 11px; text-transform: uppercase; font-weight: 700;
              color: var(--chat-accent);
              background: none; border: none; cursor: pointer;
              transition: opacity 0.15s;
            "
            class={refreshing ? "opacity-40" : "hover:opacity-80"}
          >
            <RefreshCw size={12} class={refreshing ? "animate-spin" : ""} />
            {t("refresh")}
          </button>
        </div>

        {#if serverLlmInfo}
          <table class="w-full text-xs border-collapse font-sans">
            <thead>
              <tr class="bg-(--chat-bg-secondary) border-b border-(--chat-border)">
                <th class="px-4 py-2 text-left text-[10px] uppercase font-bold text-(--chat-text-muted) w-1/3">{t("key")}</th>
                <th class="px-4 py-2 text-left text-[10px] uppercase font-bold text-(--chat-text-muted)">{t("value")}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-(--chat-border)">
              <tr>
                <td class="px-4 py-3 text-(--chat-text-muted) font-medium">{t("llmProvider")}</td>
                <td class="px-4 py-3 text-(--chat-text-primary) font-mono">{serverLlmInfo.provider}</td>
              </tr>
              <tr>
                <td class="px-4 py-3 text-(--chat-text-muted) font-medium">{t("llmModel")}</td>
                <td class="px-4 py-3 text-(--chat-text-primary) font-mono break-all">{serverLlmInfo.model}</td>
              </tr>
              {#if serverLlmInfo.visionProvider || serverLlmInfo.visionModel}
                <tr class="bg-(--chat-bg-tertiary)/30">
                  <td class="px-4 py-3 text-(--chat-accent) font-semibold italic">{t("visionProvider")}</td>
                  <td class="px-4 py-3 text-(--chat-text-primary) font-mono">{serverLlmInfo.visionProvider || serverLlmInfo.provider}</td>
                </tr>
                <tr class="bg-(--chat-bg-tertiary)/30">
                  <td class="px-4 py-3 text-(--chat-accent) font-semibold italic">{t("visionModel")}</td>
                  <td class="px-4 py-3 text-(--chat-text-primary) font-mono break-all">{serverLlmInfo.visionModel || serverLlmInfo.model}</td>
                </tr>
              {/if}
            </tbody>
          </table>
        {:else}
          <div class="p-4 text-(--chat-error) text-xs flex flex-col items-center gap-2 text-center">
            <span class="font-bold">{t("backendUnreachable")}</span>
            <p class="text-[11px] opacity-80 leading-relaxed">{t("backendUnreachableDescription")}</p>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>
